<template>
  <v-app
    id="app"
    class="application_style"
    :class="{ 'impersonation-banner-offset': showImpersonationBanner }"
    :style="impersonationBannerOffsetStyle"
  >
    <ViewAsSessionAlert
      v-if="showViewAsAlertBanner"
      ref="impersonationBanner"
      class="impersonation-active-banner"
      :title="impersonationBannerTitle"
      :description="impersonationBannerDescription"
      :button-text="impersonationBannerButtonText"
      @exit="endImpersonationSession"
    />
    <ExGenericBanner
      v-else-if="showImpersonationBanner"
      ref="impersonationBanner"
      class="impersonation-active-banner"
      :title="impersonationBannerTitle"
      :description="impersonationBannerDescription"
      :button-text="impersonationBannerButtonText"
      @apply-filter="endImpersonationSession"
    />
    <div @mouseover="handleMouseOver" @mouseleave="handleMouseLeave">
      <v-navigation-drawer
        ref="navigationBar"
        v-model="drawer"
        v-if="
          !$route.meta.hideMainSidebar &&
          smAndUpVal &&
          !isMaintenanceMode &&
          !hideMainSidebarForAppUser
        "
        app
        :mini-variant-width="88"
        :mini-variant.sync="sidebarMini"
        :width="sidebarMini ? 88 : 325"
        :permanent="(isPermanent && !drawer) || smAndUpVal"
        :temporary="isPermanent && drawer"
        id="navigation-bar"
        :data-test="role == 'admin' ? 'adminSidebarItem' : 'sidebarItem'"
        class="side-menu-nav"
      >
        <v-sheet @click="sideToggleMenu" class="fill-height">
          <SideBar
            :sidebarOption="role == 'admin' ? adminSidebarItem : sidebarItem"
            :expandOnhover="true"
            :isMini="sidebarMini"
            @toggleMenu="toggleMenu"
            @ai-assistant-open="collapseMainSidebarForAiAssistant"
            :secondSidebar="!$route.meta.secondSidebar"
            :isPermanentMini="isPermanentMini"
          ></SideBar>
        </v-sheet>
      </v-navigation-drawer>
    </div>
    <PhoneWebHeader v-if="showPhoneShell" />
    <v-main
      data-testid="main-container"
      class="ex-main-container d-flex"
      :class="[
        showIQSidePanelShell
          ? 'ex-main-container--with-iq-panel'
          : 'ex-main-container--scroll justify-center',
        { 'ex-main-container--phone-shell': showPhoneShell },
      ]"
    >
      <div
        class="ex-main-container__body d-flex justify-center"
        :class="{ 'ex-main-container__body--scroll': showIQSidePanelShell }"
      >
        <!-- Maintenance Screen -->
        <exiq-maintenance-screen v-if="isMaintenanceMode"></exiq-maintenance-screen>

        <EmployeeSideBar
          v-if="shouldShowSidebar && !isMaintenanceMode && !showPhoneShell"
          :sidebarOption="employeeSidebar"
          :employeeLoading="employeeLoading"
        />
        <NotFoundView
          v-if="getRouteError.code == 404 && getRouteError.visibility == true && !isMaintenanceMode"
        />
        <router-view
          v-if="
            (smAndUpVal || isPhoneShellRouteSupported) &&
            getRouteError.visibility == false &&
            !isMaintenanceMode
          "
          :key="$route.path"
        >
        </router-view>
        <PhoneUnsupportedState
          v-if="showPhoneUnsupportedState && getRouteError.visibility == false"
        />

        <ExLoader
          v-if="isLoading && getRouteError.visibility == false && !isMaintenanceMode"
          text="Loading..."
          :type="loaderType"
        />

        <div
          v-if="$vuetify.breakpoint.xsOnly && !isMaintenanceMode && !showPhoneShell"
          class="d-flex flex-column"
        >
          <div class="mb-4 d-flex justify-center mt-8 mb-8">
            <img src="@/assets/login/logo.svg" alt="Exiqtive logo" />
          </div>
          <div class="desktop-only">Desktop only</div>
          <div class="only-desktop-message">
            Sorry, exiqtive.com only supports access via Desktops. No mobile support at this time
          </div>
        </div>
      </div>

      <IQSidePanelShell v-if="showIQSidePanelShell" />
    </v-main>

    <!-- IQ Overlay Modal -->
    <UploadJobDescriptionFile
      v-if="iqOverlayStore.showJobDescriptionUploadDialog"
      :value="true"
      :backgroundImport="iqOverlayStore.backgroundImportEnabled"
      :positionId="iqOverlayStore.positionId"
      @discard="iqOverlayStore.closeJobDescriptionUpload"
      @uploaded="iqOverlayStore.completeJobDescriptionUpload"
    />

    <ImportJobDescriptionBackgroundOverlay
      v-if="
        iqOverlayStore.isOpen &&
        iqOverlayStore.type === 'positionImport' &&
        iqOverlayStore.backgroundImportEnabled
      "
      :modelValue="true"
      :positionId="iqOverlayStore.positionId"
      :positionName="iqOverlayStore.positionName"
      :uploadedFile="iqOverlayStore.uploadedFile"
      @close="iqOverlayStore.closeOverlay"
      @request-new-file="iqOverlayStore.reopenJobDescriptionUpload"
    />

    <ImportJobDescriptionOverlay
      v-else-if="iqOverlayStore.isOpen && iqOverlayStore.type === 'positionImport'"
      :modelValue="true"
      :positionId="iqOverlayStore.positionId"
      :positionName="iqOverlayStore.positionName"
      :uploadedFile="iqOverlayStore.uploadedFile"
      @close="iqOverlayStore.closeOverlay"
      @request-new-file="iqOverlayStore.reopenJobDescriptionUpload"
    />

    <ImportJobDescriptionIQOverlay
      v-else-if="iqOverlayStore.isOpen"
      :modelValue="iqOverlayStore.isOpen"
      :initialPrompt="iqOverlayStore.initialPrompt"
      :type="iqOverlayStore.type"
      :showSuggestions="iqOverlayStore.showSuggestions"
      :roleName="iqOverlayStore.roleName"
      :suggestedResponsibilities="iqOverlayStore.suggestedResponsibilities"
      :useCustomLoader="iqOverlayStore.useCustomLoader"
      :showCheckpointSuggestions="iqOverlayStore.showCheckpointSuggestions"
      :responsibilityName="iqOverlayStore.responsibilityName"
      :suggestedCheckpoints="iqOverlayStore.suggestedCheckpoints"
      @close="iqOverlayStore.closeOverlay"
      @submit="iqOverlayStore.emitSubmit"
      @actionClick="iqOverlayStore.emitActionClick"
      @close-navigator="iqOverlayStore.closeNavigator"
      @update:suggestions="iqOverlayStore.updateSuggestions"
      @create-and-attach="iqOverlayStore.emitCreateAndAttach"
      @reject="iqOverlayStore.emitReject"
      @suggest-more="iqOverlayStore.emitSuggestMore"
      @edit="iqOverlayStore.emitEdit"
      @close-checkpoint-navigator="iqOverlayStore.closeCheckpointNavigator"
      @update:checkpoint-suggestions="iqOverlayStore.updateCheckpointSuggestions"
    />

    <ToastContainer />
    <!-- Knock Guides (modal) global overlay -->
    <KnockGuideModal
      :guide="knockGuide"
      :step="knockGuideStep"
      :dialog="!!knockGuideStep"
      @dismiss="clearKnockGuides"
      @interact="clearKnockGuides"
    />
    <!-- No Activity Dialog -->
    <NoActivityDialog
      :show="showIdleWarning && isUserLoggedIn"
      @onStayLoggedIn="handleStayLoggedIn"
      @onLogout="handleLogout"
    />
    <SelfAppointArchitectPrompt />
  </v-app>
</template>

<script>
import SideBar from "@components/common/SideBar.vue";
import EmployeeSideBar from "@components/common/EmployeeSideBar.vue";
import ExLoader from "@components/common/ExLoader.vue";
import NotFoundView from "@/views/NotFound.vue";
import ImportJobDescriptionIQOverlay from "@components/ImportJobDescriptionIQOverlay/ImportJobDescriptionIQOverlay.vue";
import ImportJobDescriptionBackgroundOverlay from "@components/ImportJobDescriptionBackgroundOverlay/ImportJobDescriptionBackgroundOverlay.vue";
import ImportJobDescriptionOverlay from "@components/ImportJobDescriptionOverlay/ImportJobDescriptionOverlay.vue";
import UploadJobDescriptionFile from "@components/UploadJobDescriptionFile/UploadJobDescriptionFile.vue";
import ToastContainer from "@components/ConfirmationToast/ToastContainer.vue";
import NoActivityDialog from "@components/NoActivityDialog/NoActivityDialog.vue";
import SelfAppointArchitectPrompt from "@/components/SelfAppointArchitectPrompt/SelfAppointArchitectPrompt.vue";
import ExGenericBanner from "@components/GenericBanner/ExGenericBanner.vue";
import ViewAsSessionAlert from "@components/Alerts/ViewAsSessionAlert.vue";
import {
  MODE_PREVIEW,
  MODE_VIEW_AS,
  VIEW_AS_EMPLOYEES_RETURN_PATH,
  clearImpersonationStorage,
  beginEndImpersonationGuard,
  getImpersonationBannerButtonText,
  getImpersonationBannerText,
  getImpersonationBannerTitle,
  getImpersonationSession,
  isImpersonationActive,
  isInAccountReadOnlySession,
  isPreviewModeActive,
  restoreAdminContextFromStash,
} from "@components/utils/impersonationSession";
import {
  bindCanStartViewAsAccessListener,
  refreshCanStartViewAs,
} from "@components/utils/viewAsEligibility";
import { getKnock } from "@/plugins/knock";
import KnockGuideModal from "@components/KnockGuideModal/KnockGuideModal.vue";
import { useKnockGuides } from "@/composables/common/useKnockGuides";
import { patchAccessPermission } from "@/utils/accessPermissionPatch";
import { syncUserEmployeeInStorage } from "@components/utils/authEmployeeContext";
import { resetInMemoryFilterState } from "@/utils/resetInMemoryFilterState";
import { normalizeAdminPermission } from "@/utils/defaultLoggedInRedirect";
import { isProductProceduresEnabled } from "@/utils/accountProductFlags";
import {
  getOrganizationEmployeeAssignmentPath,
  shouldShowOrganizationEmployeeAssignmentsTab,
  shouldShowOrganizationEmployeeReadinessTabs,
} from "@/utils/organizationEmployeeDetailTabs";
import { resolveOrganizationReadinessCatalogRedirect } from "@/utils/organizationReadinessCatalog";
import {
  isYourStuffAllowed,
  refreshAccessPermissionFromApi,
  SYNC_ACCESS_PERMISSION_EVENT,
} from "@/utils/refreshAccessPermission";
import "vue-loading-overlay/dist/vue-loading.css";
import { createNamespacedHelpers } from "vuex";
import { getEmployeeChatter, getEmployeeBadges, stopViewAs, stopPreview } from "@/api";
import { updateAccountCountsOnLogin } from "@/utils/accountCountUtils";
import { usePositionStore } from "@/store/pinia/position";
import { useEmployeeStore } from "@/store/pinia/employee";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import { useIQSidePanelStore } from "@/store/pinia/iqSidePanel";
import IQSidePanelShell from "@components/IQSidePanel/IQSidePanelShell.vue";
import PhoneWebHeader from "@/components/PhoneWebHeader/PhoneWebHeader.vue";
import PhoneUnsupportedState from "@/components/PhoneUnsupportedState/PhoneUnsupportedState.vue";
import { isPhoneShellRoute } from "@/utils/phoneShell";
import { getCurrentInstance } from "vue";
import { useNavigateToSectionTool } from "@components/webmcp/navigation/useNavigateToSectionTool.js";
import {
  LD_FLAG_ACCOUNT_ACTIVITY_STREAM,
  LD_FLAG_DRAG_DROP_ATTACH_ORG_OBJECTS_POSITION_TREE_VIEW,
  LD_FLAG_IDEA_MDP_546,
  LD_FLAG_IDEA_MDP_577,
  LD_FLAG_IDEA_MDP_582,
  LD_FLAG_IDEA_MDP_599,
  LD_FLAG_IDEA_MDP_650,
} from "@components/constants/launchDarklyFlagKeys";
import { ALL_TASKS_LABEL, TASKS_MODULE_LD_FLAG } from "@components/constants/tasksModule";
const { mapState: mapDataState } = createNamespacedHelpers("isPosition");

// ─── redirectIfTabsDisabled helpers ──────────────────────────────────────────

function getSettings432RedirectPath(pathNorm, permissions, tabs) {
  if (pathNorm !== "/settings") return undefined;
  const { ideaMDP432, isOwnerOrCoOwner, isAdmin, hasReadinessAccess, yourStuffAllowed } =
    permissions;
  const {
    showNewOrganizationTab,
    showNewYourStuffTab,
    showNewReadinessTab,
    showPerformanceTab,
    showFairPayTab,
    showProceduresTab,
  } = tabs;
  if (!ideaMDP432 || isOwnerOrCoOwner) return undefined;
  const candidates = [
    [showNewOrganizationTab && (isOwnerOrCoOwner || isAdmin), "/organization/dashboard"],
    [yourStuffAllowed && showNewYourStuffTab, "/your-stuff/employee/responsibilities"],
    [showNewReadinessTab && (isOwnerOrCoOwner || hasReadinessAccess), "/new-readiness"],
    [showPerformanceTab && (isOwnerOrCoOwner || isAdmin), "/performance"],
    [showFairPayTab && (isOwnerOrCoOwner || isAdmin), "/fair-pay"],
    [showProceduresTab && (isOwnerOrCoOwner || isAdmin), "/procedures"],
  ];
  const match = candidates.find(([cond]) => cond);
  return match ? match[1] : "/404";
}

function getTabOffRedirectPath(pathNorm, isOwnerOrCoOwner, tabs) {
  const { showNewReadinessTab, showPerformanceTab, showFairPayTab, showProceduresTab } = tabs;
  // Prefer Organization for elevated users; do not fall back to Your Stuff without permission.
  const fallbackPath =
    (isOwnerOrCoOwner && "/organization/dashboard") ||
    (showFairPayTab && "/fair-pay") ||
    (isOwnerOrCoOwner && "/settings") ||
    "/404";
  if (pathNorm === "/new-readiness" && !showNewReadinessTab) {
    return (showPerformanceTab && "/performance") || fallbackPath;
  }
  if (pathNorm === "/performance" && !showPerformanceTab) return fallbackPath;
  if (
    (pathNorm === "/fair-pay" && !showFairPayTab) ||
    (pathNorm === "/fair-pay-coming-soon" && !showFairPayTab) ||
    (pathNorm === "/procedures" && !showProceduresTab)
  ) {
    return (
      (isOwnerOrCoOwner && "/organization/dashboard") || (isOwnerOrCoOwner && "/settings") || "/404"
    );
  }
  return undefined;
}

function getYourStuffDeniedRedirectPath(pathNorm, permissions, tabs) {
  if (!pathNorm.startsWith("/your-stuff")) return undefined;
  const { yourStuffAllowed, isOwnerOrCoOwner, isAdmin, hasReadinessAccess } = permissions;
  const {
    showNewYourStuffTab,
    showNewOrganizationTab,
    showNewReadinessTab,
    showPerformanceTab,
    showFairPayTab,
    ideaMDP432,
  } = tabs;
  if (yourStuffAllowed && showNewYourStuffTab) return undefined;
  if (showNewOrganizationTab && (isOwnerOrCoOwner || (ideaMDP432 && isAdmin))) {
    return "/organization/dashboard";
  }
  if (showNewReadinessTab && (isOwnerOrCoOwner || hasReadinessAccess)) return "/new-readiness";
  if (showPerformanceTab && (isOwnerOrCoOwner || isAdmin)) return "/performance";
  if (showFairPayTab && (isOwnerOrCoOwner || isAdmin)) return "/fair-pay";
  if (isOwnerOrCoOwner) return "/settings";
  return "/404";
}

function getOrganizationRedirectPath(permissions, tabs) {
  const { isOwnerOrCoOwner, isAdmin, yourStuffAllowed, hasReadinessAccess } = permissions;
  const {
    showNewOrganizationTab,
    showNewYourStuffTab,
    showNewReadinessTab,
    showPerformanceTab,
    showFairPayTab,
    ideaMDP432,
  } = tabs;
  if (showNewOrganizationTab && (isOwnerOrCoOwner || (ideaMDP432 && isAdmin))) return undefined;
  if (yourStuffAllowed && showNewYourStuffTab) return "/your-stuff/employee/responsibilities";
  if (showNewReadinessTab && (isOwnerOrCoOwner || hasReadinessAccess)) return "/new-readiness";
  if (showPerformanceTab) return "/performance";
  if (showFairPayTab) return "/fair-pay";
  if (isOwnerOrCoOwner) return "/settings";
  return "/404";
}

// ─── sidebarItem helpers ──────────────────────────────────────────────────────

// While the account's Procedures product is off, the entry is the administrators' upsell.
function getProceduresNavItem(navItems, productProcedures) {
  return productProcedures
    ? navItems.proceduresItem
    : { ...navItems.proceduresItem, to: "/procedures-coming-soon" };
}

function populateLegacySidebarItems(val, navItems, permissions, flags) {
  const { currentAccount, isOwnerOrCoOwner, hasElevatedNav, hasReadinessAccess } = permissions;
  const {
    showNewYourStuffTab,
    showNewOrganizationTab,
    showProceduresTab,
    productProcedures,
    showNewReadinessTab,
    showPerformanceTab,
    showFairPayTab,
    ideaMDP575,
    showAccountActivityStream,
  } = flags;
  const ownerNavItems = [
    showNewOrganizationTab && navItems.organizationItem,
    showProceduresTab && getProceduresNavItem(navItems, productProcedures),
    showPerformanceTab && navItems.performanceItem,
    showFairPayTab && navItems.fairPayItem,
    ideaMDP575 && isOwnerOrCoOwner && navItems.tasksItem,
  ].filter(Boolean);
  if (currentAccount && showNewYourStuffTab && isYourStuffAllowed(currentAccount))
    val.tItems.push(navItems.newYourStuff);
  if (currentAccount && isOwnerOrCoOwner) val.tItems.push(...ownerNavItems);
  if (hasReadinessAccess && showNewReadinessTab) val.tItems.push(navItems.readinessItem);
  if (hasElevatedNav && showAccountActivityStream) val.bItems.push(navItems.activityItem);
  if (currentAccount && isOwnerOrCoOwner) val.bItems.push(navItems.settingsItem);
}

function populateMdp432SidebarItems(val, navItems, permissions, flags) {
  const { currentAccount, isOwnerOrCoOwner, hasElevatedNav } = permissions;
  const {
    showNewYourStuffTab,
    showNewOrganizationTab,
    showProceduresTab,
    productProcedures,
    showNewReadinessTab,
    showPerformanceTab,
    showFairPayTab,
    ideaMDP575,
    showAccountActivityStream,
  } = flags;
  const yourStuffAllowed = isYourStuffAllowed(currentAccount);
  const elevatedItems = [
    showNewOrganizationTab && navItems.organizationItem,
    showProceduresTab && getProceduresNavItem(navItems, productProcedures),
    showNewReadinessTab && navItems.readinessItem,
    showPerformanceTab && navItems.performanceItem,
    showFairPayTab && navItems.fairPayItem,
    ideaMDP575 && navItems.tasksItem,
  ].filter(Boolean);
  if (currentAccount && showNewYourStuffTab && yourStuffAllowed)
    val.tItems.push(navItems.newYourStuff);
  if (hasElevatedNav) val.tItems.push(...elevatedItems);
  if (hasElevatedNav && showAccountActivityStream) val.bItems.push(navItems.activityItem);
  if (currentAccount && isOwnerOrCoOwner) val.bItems.push(navItems.settingsItem);
}

export { populateLegacySidebarItems, populateMdp432SidebarItems };

// ─────────────────────────────────────────────────────────────────────────────

export default {
  name: "App",
  setup() {
    const { proxy } = getCurrentInstance();
    useNavigateToSectionTool({ proxy });
    const {
      activeGuide: knockGuide,
      activeStep: knockGuideStep,
      initialize: initializeKnockGuides,
      clear: clearKnockGuides,
      cleanup: cleanupKnockGuides,
    } = useKnockGuides();
    return {
      knockGuide,
      knockGuideStep,
      initializeKnockGuides,
      clearKnockGuides,
      cleanupKnockGuides,
    };
  },
  components: {
    SideBar,
    ExLoader,
    EmployeeSideBar,
    NotFoundView,
    ImportJobDescriptionIQOverlay,
    ImportJobDescriptionBackgroundOverlay,
    ImportJobDescriptionOverlay,
    UploadJobDescriptionFile,
    ToastContainer,
    NoActivityDialog,
    SelfAppointArchitectPrompt,
    ExGenericBanner,
    ViewAsSessionAlert,
    KnockGuideModal,
    IQSidePanelShell,
    PhoneWebHeader,
    PhoneUnsupportedState,
  },
  data: () => ({
    knockFeed: null,
    currentAccountDetails: {},
    isHovered: false,
    drawer: false,
    sidebarMini: true,
    toggleMenuClicked: false,
    secondEmpBar: false,
    loader: "spinner",
    isOtherSidebarPresent: false,
    positionStore: usePositionStore(),
    employeeStore: useEmployeeStore(),
    sidebarCountStore: useSidebarCountStore(),
    iqOverlayStore: useIQOverlayStore(),
    iqSidePanelStore: useIQSidePanelStore(),
    isSidebarCollapsedPermanently: false, // Set to false to make it open by default
    // ER-10088: auto-collapse main side nav while a responsibility is open in the tree.
    responsibilityCollapseActive: false, // true while we are force-collapsing for an open responsibility
    preResponsibilityCollapseState: false, // user's collapse preference before the responsibility was opened
    mainSidebarRouteInitialized: false, // skip auto-collapse on the first (load/refresh) route run
    role: "",
    showPositionHistoryTab: false,
    showReadinessOrganizationTab: false,
    showNewOrganizationTab: true,
    showNewYourStuffTab: false,
    showNewReadinessTab: false,
    showProceduresTab: false,
    showFairPayTab: false,
    showPerformanceTab: false,
    tabFlagsLoaded: false,
    adminSidebarItem: {
      type: "main",
      tItems: [
        {
          name: "ClientLogo",
          img: "clientlogo.svg",
          nameImg: "clientlogoname.svg",
          to: "",
        },
        {
          name: "Home",
          img: "home-01.svg",
          to: "/",
        },
        {
          name: "Accounts",
          img: "Company with Employees-grey.svg",
          to: "",
          count: "10",
        },
        {
          name: "Products",
          img: "box.svg",
          to: "",
          count: "10",
        },
        {
          name: "Subscriptions",
          img: "Subscription-grey.svg",
          to: "",
          count: "10",
        },
        {
          name: "Users",
          img: "users-01.svg",
          to: "",
          count: "10",
        },
        {
          name: "Partners",
          img: "Partnership-grey.svg",
          to: "",
          count: "10",
        },
      ],
      bItems: [
        {
          name: "Support",
          img: "life-buoy.svg",
          to: "",
        },
        {
          name: "Settings",
          img: "settings.svg",
          to: "",
        },
      ],
      cardItem: [
        {
          name: "Olivia Rhye",
          email: "olivia@untitledui.com",
          img: "Avatar.svg",
          to: "/login",
        },
      ],
    },
    idleTime: null,
    idleWarningTime: null,
    showIdleWarning: false,
    employeesData: [],
    employeeLoading: false,
    totalChatterCount: 0,
    activeAssignments: 0,
    currentReadiness: 0,
    accessPermissionsValue: null,
    /** ER-11470 / MDP-599 — collapse SideMenu to an inline Account for App Users on Your Stuff. */
    ideaMDP599: false,
    /** ER-12834 / MDP-650 — phone browser shell (minimal header, unsupported-route state). */
    ideaMDP650: false,
    impersonationCountdownTimer: null,
    endingImpersonationSession: false,
    impersonationBannerHeightPx: 110,
  }),
  computed: {
    isImpersonating() {
      // Driven by the active session (set at handoff), NOT the LD flag. The flag gates
      // entry (admin dropdown + Login.vue handoff); once a session is active the banner
      // and End control must stay available for the whole session (rules §3/§7). The
      // flag is re-evaluated for the impersonated account and can resolve false there.
      return isImpersonationActive();
    },
    showImpersonationBanner() {
      return this.isImpersonating;
    },
    showViewAsAlertBanner() {
      if (!this.showImpersonationBanner) return false;
      const session = getImpersonationSession();
      return session?.mode === MODE_VIEW_AS || session?.mode === MODE_PREVIEW;
    },
    impersonationBannerDescription() {
      return getImpersonationBannerText(getImpersonationSession());
    },
    impersonationBannerButtonText() {
      return getImpersonationBannerButtonText(getImpersonationSession());
    },
    impersonationBannerTitle() {
      return getImpersonationBannerTitle(getImpersonationSession());
    },
    impersonationBannerOffsetStyle() {
      if (!this.showImpersonationBanner) return {};
      return {
        "--impersonation-banner-height": `${this.impersonationBannerHeightPx}px`,
      };
    },
    isPreviewMode() {
      // Preview has no Access — Your Stuff only. View-as uses the impersonated
      // user's real permissions (ER-11088).
      return isPreviewModeActive();
    },
    getRouteError() {
      return this.$store.state.routeErrorHandling.error;
    },
    isMaintenanceMode() {
      return this.$store.state.isLoading.maintenanceMode;
    },
    isLoading() {
      return this.$store.state.isLoading.isLoading;
    },
    loaderType() {
      return this.$store.state.isLoading.loaderType;
    },
    storePositions() {
      return this.$store.getters["positionStore/positions"] || [];
    },
    currentEmployeeReadiness() {
      return this.$store.getters["employeeStore/currentEmployeeReadiness"] ?? 0;
    },
    currentEmployeeBadges() {
      return this.$store.getters["employeeStore/currentEmployeeBadges"];
    },
    ...mapDataState(["activePositionsCount"]),
    /**
     * ER-11470 / MDP-599 — an App User (admin_permission "None") sees no admin SideMenu
     * sections, so on Your Stuff the whole rail is hidden and replaced by the inline
     * Account cluster + floating AI Assistant rendered by YourStuffPageHeader/YourStuffPage.
     */
    hideMainSidebarForAppUser() {
      const path = this.$route?.path || "";
      if (!path.startsWith("/your-stuff")) return false;
      if (!this.ideaMDP599) return false;
      // Keep the rail until the Access loads — otherwise an admin's SideMenu
      // would flash away and back during the async Access sync on mount.
      if (!this.accessPermissionsValue) return false;
      return !normalizeAdminPermission(this.accessPermissionsValue).hasElevatedNav;
    },
    sidebarItem() {
      const currentAccount = this.accessPermissionsValue;
      const isOwner = currentAccount?.admin_permission === "owner";
      const isCoOwner = currentAccount?.admin_permission === "co-owner";
      const isAdmin = currentAccount?.admin_permission === "admin";
      const ideaMDP432 = this.$store.getters["featureFlags/ideaMDP432"];
      const ideaMDP575 = this.$store.getters["featureFlags/ideaMDP575"];
      const showAccountActivityStream = this.$store.getters["featureFlags/ideaMDP404"];
      const isOwnerOrCoOwner = isOwner || isCoOwner;
      const hasElevatedNav = currentAccount && (isOwner || isCoOwner || isAdmin);
      const hasReadinessAccess =
        (currentAccount && isOwner) ||
        currentAccount?.is_co_owner ||
        currentAccount?.has_readiness_access;

      const val = {
        type: "main",
        tItems: [{ name: "ClientLogo", img: "home-01.svg", nameImg: "new-logo.svg", to: "" }],
        bItems: [],
        cardItem: [],
      };

      const navItems = {
        newYourStuff: { name: "Your Stuff", img: "MyStuff.svg", to: "/your-stuff" },
        organizationItem: {
          name: "Organization",
          img: "Organization-chart.svg",
          to: "/organization/dashboard",
        },
        proceduresItem: { name: "Procedures", img: "procedures.svg", to: "/procedures" },
        readinessItem: { name: "Readiness", img: "Readiness.svg", to: "/new-readiness" },
        performanceItem: { name: "Performance", img: "Performance.svg", to: "/performance" },
        fairPayItem: { name: "Fair Pay", img: "FairPay.svg", to: "/fair-pay" },
        tasksItem: { name: ALL_TASKS_LABEL, img: "Tasks.svg", to: "/tasks" },
        activityItem: { name: "Activity", img: "activity.svg", to: "/activity" },
        settingsItem: { name: "Settings", img: "settings.svg", to: "/settings" },
      };

      if (currentAccount?.user) {
        val.cardItem.push({
          name: `${currentAccount.user.first_name} ${currentAccount.user.last_name}`,
          firstName: currentAccount.user.first_name,
          lastName: currentAccount.user.last_name,
          email: currentAccount.user.email,
          accountName: currentAccount.account_name,
          img: "",
          to: "/login",
        });
      }

      const permissions = {
        currentAccount,
        isOwner,
        isCoOwner,
        isOwnerOrCoOwner,
        isAdmin,
        hasElevatedNav,
        hasReadinessAccess,
      };
      const flags = {
        showNewYourStuffTab: this.showNewYourStuffTab,
        showNewOrganizationTab: this.showNewOrganizationTab,
        showProceduresTab: this.showProceduresTab,
        productProcedures: isProductProceduresEnabled(this.currentAccountDetails),
        showNewReadinessTab: this.showNewReadinessTab,
        showPerformanceTab: this.showPerformanceTab,
        showFairPayTab: this.showFairPayTab,
        ideaMDP575,
        showAccountActivityStream,
      };

      // Preview (ER-10317): Your Stuff only — no org nav, settings, or profile card.
      if (this.isPreviewMode) {
        val.tItems = [{ name: "ClientLogo", img: "home-01.svg", nameImg: "new-logo.svg", to: "" }];
        if (this.showNewYourStuffTab) {
          val.tItems.push(navItems.newYourStuff);
        }
        val.bItems = [];
        val.cardItem = [];
        return val;
      }

      if (ideaMDP432) {
        populateMdp432SidebarItems(val, navItems, permissions, flags);
      } else {
        populateLegacySidebarItems(val, navItems, permissions, flags);
      }

      return val;
    },
    employeeSidebar() {
      const empId = this.$route.params.id;
      localStorage.setItem("emp_id", empId);
      const readinessProductOn = shouldShowOrganizationEmployeeReadinessTabs(
        this.currentAccountDetails
      );
      const baseItems = [];

      if (readinessProductOn) {
        baseItems.push({
          name: "Path to Readiness",
          img: "Readiness.svg",
          to: `/organization/employees/${empId}/pathtoreadiness`,
          count: `${this.currentEmployeeReadiness}%`,
        });
      }

      if (shouldShowOrganizationEmployeeAssignmentsTab(this.showPositionHistoryTab)) {
        baseItems.push({
          name: "Positions History",
          img: "Assignment.svg",
          to: getOrganizationEmployeeAssignmentPath(empId),
          count: this.$store.getters["employeeStore/currentPositionHistoryActiveCount"],
        });
      }

      if (readinessProductOn) {
        baseItems.push({
          name: "Badges",
          img: "Badge.svg",
          to: `/organization/employees/${empId}/Badges`,
          count: `${this.$store.getters["employeeStore/currentEmployeeTotalBadges"]}`,
        });
      }

      const val = {
        type: "secondary",
        tItems: baseItems
          .filter((item) => item.name !== "Chatter")
          .concat({
            name: "Chatter",
            img: "Chatter.svg",
            to: `/organization/employees/${empId}/chatter`,
            count: this.$store.getters["employeeStore/currentEmployeeChatterCount"],
          }),
        cardItem: [
          {
            firstName: this.employeesData ? this.employeesData.first_name : "",
            lastName: this.employeesData ? this.employeesData.last_name : "",
            position: this.employeesData ? this.employeesData.title : "",
            email: "",
            photoUrl: this.employeesData ? this.employeesData.avatar : "",
            showAvailability: false,
            to: "/organization/employees",
            access: "has-access",
            status: this.employeesData ? this.employeesData.current_status : "",
            archived: this.employeesData ? this.employeesData.archived : "",
          },
        ],
        empData: this.employeesData,
      };
      return val;
    },
    smAndUpVal() {
      return this.$vuetify.breakpoint.smAndUp;
    },
    lgAndUpVal() {
      return this.$vuetify.breakpoint.lgAndUp;
    },
    isPermanent() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    // A new computed property to pass to SideBar to control icon display
    isPermanentMini() {
      return this.isSidebarCollapsedPermanently && !this.$route.meta.secondSidebar;
    },
    shouldShowSidebar() {
      const shouldShow =
        this.$route.meta.secondSidebar === "employee" &&
        !this.$route.meta.hideSidebar?.() &&
        this.$store.getters["featureFlags/showEmployeeSidebar"];
      return shouldShow;
    },
    // ER-11834: only employee detail routes (/organization/employees/:id) carry an
    // employee id in `$route.params.id`. Other routes (job-families, job-categories)
    // reuse `:id` for a non-employee entity, and treating it as an employee id fires
    // a doomed GET employee/:id/ request on every page load.
    isEmployeeDetailRoute() {
      return this.$route?.meta?.secondSidebar === "employee";
    },
    isUserLoggedIn() {
      const token = localStorage.getItem("token");
      const currentAccountID = localStorage.getItem("currentAccountID");
      const isLoginPage = this.$route.path === "/login";
      return !!(token && currentAccountID) && !isLoginPage;
    },
    /** ER-12834 / MDP-650 — phone shell for every signed-in user (administrators included). */
    showPhoneShell() {
      return (
        this.ideaMDP650 &&
        this.$vuetify.breakpoint.xsOnly &&
        this.isUserLoggedIn &&
        !this.isMaintenanceMode
      );
    },
    isPhoneShellRouteSupported() {
      return this.showPhoneShell && isPhoneShellRoute(this.$route?.path);
    },
    showPhoneUnsupportedState() {
      return this.showPhoneShell && !isPhoneShellRoute(this.$route?.path);
    },
    /** LD keys with dots/dashes cannot use string path `flags["..."]` on Vue 2 watch — use computeds instead. */
    ldFlagEmployeePageHorizontalMenu() {
      return this.flags?.["employee.page_horizontal_menu.enabled"];
    },
    ldFlagEmployeePositionHistoryView() {
      return this.flags?.["employee-position-history-view"];
    },
    ldFlagIdeaMdp412() {
      return this.flags?.["idea-MDP-412"];
    },
    ldFlagIdeaMdp378() {
      return this.flags?.["idea-MDP-378"];
    },
    ldFlagIdeaMdp381() {
      return this.flags?.["idea-MDP-381"];
    },
    ldFlagIdeaMdp582() {
      return this.flags?.[LD_FLAG_IDEA_MDP_582];
    },
    showIQSidePanelShell() {
      return (
        this.isUserLoggedIn &&
        this.iqSidePanelStore.isOpen &&
        this.$store.getters["featureFlags/ideaMDP582"] === true
      );
    },
    ldFlagIdeaMdp432() {
      return this.flags?.["idea-MDP-432"];
    },
    ldFlagIdeaMdp438() {
      return this.flags?.["idea-MDP-438"];
    },
    ldFlagMyStuffNewYourStuff() {
      return this.flags?.["my-stuff.new-your-stuff.enabled"];
    },
    ldFlagNewReadiness() {
      return this.flags?.["new-readiness"];
    },
    ldFlagProceduresEnabled() {
      return this.flags?.["procedures.enabled"];
    },
    ldFlagFairPayEnabled() {
      return this.flags?.["fair-pay.enabled"];
    },
    ldFlagPerformanceEnabled() {
      return this.flags?.["performance.enabled"];
    },
  },
  async mounted() {
    // Initialize LaunchDarkly context on app start
    await this.initializeLaunchDarklyContext();

    this.role = this.$route.meta.role;
    const localStorageToken = localStorage.getItem("token");
    await this.getAccountCount();

    // Watch the employee page horizontal menu flag reactively
    await this.$watchFlag("employee.page_horizontal_menu.enabled", false);
    // Watch the employee position history view flag
    await this.$watchFlag("employee-position-history-view", false);
    // Watch the catalogs dashboard flag
    await this.$watchFlag("catalogs.dashboard.enabled", false);
    // Watch the idea-MDP-412 flag (Vuex: ideaMDP412 / SET_IDEA_MDP_412)
    await this.$watchFlag("idea-MDP-412", false);
    // Watch the idea-MDP-378 flag
    await this.$watchFlag("idea-MDP-378", false);

    // Watch the idea-MDP-381 flag
    await this.$watchFlag("idea-MDP-381", false);
    await this.$watchFlag(LD_FLAG_IDEA_MDP_582, false);
    await this.$watchFlag("idea-MDP-432", false);
    await this.$watchFlag("idea-MDP-438", false);
    await this.$watchFlag("idea-MDP-453", false);
    // Watch new Your Stuff sidebar item flag
    await this.$watchFlag("my-stuff.new-your-stuff.enabled", false);
    // Watch new Readiness sidebar item flag
    await this.$watchFlag("new-readiness", false);
    // Initialize the new Procedures sidebar item flag
    this.showProceduresTab = await this.$getFlag("procedures.enabled", false);
    // Initialize the position history feature flag
    this.showPositionHistoryTab = await this.$getFlag("employee-position-history-view", false);
    this.showReadinessOrganizationTab = await this.$getFlag("hide_organizational_readiness", false);
    // Initialize the new Your Stuff sidebar item flag
    this.showNewYourStuffTab = await this.$getFlag("my-stuff.new-your-stuff.enabled", false);
    // Initialize the fair pay sidebar item flag
    this.showFairPayTab = await this.$getFlag("fair-pay.enabled", false);
    // Initialize the performance sidebar item flag
    this.showPerformanceTab = await this.$getFlag("performance.enabled", false);
    // Initialize the new Readiness sidebar item flag
    this.showNewReadinessTab = await this.$getFlag("new-readiness", false);
    // Initialize the new Procedures sidebar item flag
    this.showProceduresTab = await this.$getFlag("procedures.enabled", false);
    this.tabFlagsLoaded = true;
    this.ideaMDP599 = await this.$getFlag(LD_FLAG_IDEA_MDP_599, true);
    this.ideaMDP650 = await this.$getFlag(LD_FLAG_IDEA_MDP_650, false);
    this.redirectIfTabsDisabled();
    const ideaMDP427Flag = await this.$getFlag("idea-MDP-427", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_427", ideaMDP427Flag);

    const ideaMDP389Flag = await this.$getFlag("idea-MDP-389", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_389", ideaMDP389Flag);
    // Initialize the idea-MDP-378 flag
    const ideaMDP378Flag = await this.$getFlag("idea-MDP-378", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_378", ideaMDP378Flag);
    // Initialize the idea-MDP-381 flag
    const ideaMDP381Flag = await this.$getFlag("idea-MDP-381", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_381", ideaMDP381Flag);
    const ideaMDP582Flag = await this.$getFlag(LD_FLAG_IDEA_MDP_582, false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_582", ideaMDP582Flag);
    const ideaMDP449Flag = await this.$getFlag("idea-MDP-449", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_449", ideaMDP449Flag);

    const ideaMDP176Flag = await this.$getFlag("idea-MDP-176", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_176", ideaMDP176Flag);
    const ideaMDP432Flag = await this.$getFlag("idea-MDP-432", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_432", ideaMDP432Flag);
    const ideaMDP442Flag = await this.$getFlag("idea-MDP-442", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_442", ideaMDP442Flag);
    const ideaMDP441Flag = await this.$getFlag("idea-MDP-441", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_441", ideaMDP441Flag);
    const ideaMDP450Flag = await this.$getFlag("idea-MDP-450", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_450", ideaMDP450Flag);
    const ideaMDP453Flag = await this.$getFlag("idea-MDP-453", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_453", ideaMDP453Flag);
    const ideaMDP452Flag = await this.$getFlag("idea-MDP-452", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_452", ideaMDP452Flag);
    const ideaMDP577Flag = await this.$getFlag(LD_FLAG_IDEA_MDP_577, false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_577", ideaMDP577Flag);
    const ideaMDP461Flag = await this.$getFlag("idea-MDP-461", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_461", ideaMDP461Flag);
    const ideaMDP546Flag = await this.$getFlag(LD_FLAG_IDEA_MDP_546, false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_546", ideaMDP546Flag);
    const ideaMDP575Flag = await this.$getFlag(TASKS_MODULE_LD_FLAG, false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_575", ideaMDP575Flag);
    const ideaMDP438Flag = await this.$getFlag("idea-MDP-438", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_438", ideaMDP438Flag);
    const ideaMDP404Flag = await this.$getFlag(LD_FLAG_ACCOUNT_ACTIVITY_STREAM, false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_404", ideaMDP404Flag);
    const dragDropPositionTreeFlag = await this.$getFlag(
      LD_FLAG_DRAG_DROP_ATTACH_ORG_OBJECTS_POSITION_TREE_VIEW,
      false
    );
    this.$store.commit(
      "featureFlags/SET_DRAG_DROP_ATTACH_ORG_OBJECTS_POSITION_TREE_MDP434",
      dragDropPositionTreeFlag
    );
    if (
      !this.showPositionHistoryTab &&
      this.$route.name === "EmployeeAssignments" &&
      this.$route.params?.id
    ) {
      this.$router.push({
        name: "EmployeeBadges",
        params: { id: this.$route?.params?.id },
      });
    }

    // Initialize feature flags
    await this.$store.dispatch("featureFlags/checkFeatureFlags", this.$route);
    if (this.isImpersonating) {
      this.startImpersonationCountdown();
      this.$nextTick(() => this.syncImpersonationBannerOffset());
    }

    this._onImpersonationReadOnly = (event) => {
      const message =
        event?.detail?.message || "You're in a read-only session — this action is unavailable.";
      this.$notifications?.error?.(message);
    };
    window.addEventListener("impersonation-read-only", this._onImpersonationReadOnly);

    const patchUserAccessEnabled = await this.$getFlag("patch-user-access", false);
    await this.syncAccessPermission(patchUserAccessEnabled);
    bindCanStartViewAsAccessListener();
    refreshCanStartViewAs();

    this.$root.$on("accessPermissionUpdated", (data) => {
      this.accessPermissionsValue = data;
      // ER-10191: drop previous Access in-memory filter state before reload.
      resetInMemoryFilterState();
      // ER-10192: Access changed — notify listeners to reload filters from Access-scoped storage.
      try {
        window.dispatchEvent(
          new CustomEvent("exiqtive-access-filters-changed", {
            detail: { accessId: data?.id ?? null },
          })
        );
      } catch (e) {
        /* ignore */
      }
    });

    this.$root.$on(SYNC_ACCESS_PERMISSION_EVENT, this.onSyncAccessPermission);

    this.$root.$on("currentAccountDetailsUpdated", (data) => {
      this.currentAccountDetails = data;
      this.redirectIfTabsDisabled();
    });

    // Listen for login event to refresh LaunchDarkly context and flags
    this.$root.$on("userLoggedIn", async () => {
      await this.refreshFlagsAfterLogin();
      await this.syncAccessPermission();
    });
    if (this.$route.params) {
      const empId = this.$route.params.id;
      if (empId && localStorageToken) {
        await this.getEmployees(empId);
      }
    }
    try {
      this.currentAccountDetails =
        JSON.parse(localStorage.getItem("currentAccountDetails") || "{}") || {};
    } catch {
      this.currentAccountDetails = {};
    }
    this.startIdleTimer();
    document.addEventListener("click", this.handleOutsideClick);

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      console.warn("[Knock] No user found in localStorage, skipping init");
      return;
    }
    const knock = getKnock();
    if (!knock) {
      console.warn("[Knock] Client not initialized - missing API key");
      return;
    }
    await knock.authenticate(user.email);
    if (this.knockFeed) {
      this.knockFeed.teardown();
      this.knockFeed = null;
    }

    const feedChannelId = "7b3b267a-30b1-4de4-ba06-bc8b60c438cc";
    this.knockFeed = knock.feeds.initialize(feedChannelId, {
      page_size: 20,
      archived: "exclude",
    });

    // ER-11313: Knock realtime feed items are never surfaced as toasts. The previous
    // catch-all handler toasted every feed item with its workflow template copy (e.g.
    // "Hey there 👋 - there's a new activity in your workspace."), including primary
    // position assignment audit events. Received items are still archived so the
    // in-app notification center stays clean; nothing is toasted.
    this.knockFeed.on("items.received.realtime", async ({ items }) => {
      if (!items?.length) return;
      await this.knockFeed.markAllAsArchived();
    });

    await this.knockFeed.fetch();
    this.knockFeed.listenForUpdates();

    // ER-11934: init the Knock Guides client (modal guides) alongside the Feed
    // channel. This is the guide counterpart of the feed wiring above — it was
    // never built, so in-app Guides (e.g. "Quizzes for Checkpoints") never render.
    this.initializeKnockGuides(knock);
  },
  beforeDestroy() {
    if (this.impersonationCountdownTimer) {
      clearInterval(this.impersonationCountdownTimer);
    }
    if (this._onImpersonationReadOnly) {
      window.removeEventListener("impersonation-read-only", this._onImpersonationReadOnly);
    }
    if (this.knockFeed) {
      this.knockFeed.teardown();
      this.knockFeed = null;
    }
    this.cleanupKnockGuides();
    document.removeEventListener("click", this.handleOutsideClick);
    this.$root.$off("accessPermissionUpdated");
    this.$root.$off(SYNC_ACCESS_PERMISSION_EVENT, this.onSyncAccessPermission);
    this.$root.$off("currentAccountDetailsUpdated");
    this.$root.$off("userLoggedIn");
    // Clean up timers
    clearTimeout(this.idleTime);
    clearTimeout(this.idleWarningTime);
    // Remove idle timer event listeners
    window.removeEventListener("load", this.resetTimer, true);
    window.removeEventListener("mousemove", this.resetTimer, true);
    window.removeEventListener("mousedown", this.resetTimer, true);
    window.removeEventListener("click", this.resetTimer, true);
    window.removeEventListener("keydown", this.resetTimer, true);
    window.removeEventListener("scroll", this.resetTimer, true);
  },
  watch: {
    "iqOverlayStore.isOpen"(isOpen) {
      if (isOpen && this.iqSidePanelStore.isOpen) {
        this.iqSidePanelStore.closeSidePanel();
      }
    },
    showImpersonationBanner(isShowing) {
      if (isShowing) {
        this.$nextTick(() => this.syncImpersonationBannerOffset());
      } else {
        this.impersonationBannerHeightPx = 110;
      }
    },
    impersonationBannerDescription() {
      if (this.showImpersonationBanner) {
        this.$nextTick(() => this.syncImpersonationBannerOffset());
      }
    },
    drawer(val) {
      if (val && this.isPermanent) {
        document.addEventListener("click", this.handleClickOutside);
      } else {
        document.removeEventListener("click", this.handleClickOutside);
      }
    },
    $route: {
      immediate: true,
      async handler(newVal) {
        // Session ended (idle timeout, 401 interceptor, manual logout) and the
        // app routed to /login: reset the docked IQ side panel so it neither
        // renders over the login page nor reopens on the next sign-in.
        if (!this.isUserLoggedIn && this.iqSidePanelStore.isOpen) {
          this.iqSidePanelStore.closeSidePanel();
        }

        let secondSidebar = newVal.meta.secondSidebar;

        this.secondEmpBar = !!secondSidebar;
        this.isOtherSidebarPresent = this.$route.meta.sideBarPresent;
        this.role = this.$route.meta.role;
        if (!this.isUserLoggedIn) {
          try {
            this.accessPermissionsValue = JSON.parse(localStorage.getItem("accessPermission"));
          } catch {
            this.accessPermissionsValue = null;
          }
        }

        // The impersonation handoff applies the session then navigates (SPA), after
        // App is already mounted — start the countdown here so it ticks immediately.
        if (isImpersonationActive() && !this.impersonationCountdownTimer) {
          this.startImpersonationCountdown();
        }

        if (secondSidebar === "employee") {
          this.sidebarMini = true;
          this.drawer = false;
        } else {
          this.applyMainSidebarStateForRoute(newVal);
        }

        // Check feature flags for employee detail pages and Your Stuff (LD context + tab flags).
        const onYourStuff =
          typeof newVal.path === "string" && newVal.path.startsWith("/your-stuff");
        if (newVal.meta.secondSidebar === "employee" || onYourStuff) {
          await this.$store.dispatch("featureFlags/checkFeatureFlags", newVal);
        } else {
          // Update sidebar visibility for other routes
          this.$store.dispatch("featureFlags/updateSidebarVisibility", newVal);
        }
        this.redirectIfTabsDisabled();
      },
      deep: true,
    },
    "$vuetify.breakpoint.width"() {
      if (this.$route.meta.secondSidebar == "employee") {
        this.sidebarMini = true;
        if (this.sidebarMini) {
          this.drawer = true;
        }
        this.secondEmpBar = false;
      } else {
        this.sidebarMini = this.isSidebarCollapsedPermanently; // Ensure default is collapsed unless explicitly expanded
        if (this.sidebarMini) {
          this.drawer = false;
        }
        this.secondEmpBar = false;
      }
    },
    storePositions() {
      this.updateActiveAssignments();
    },
    "$route.params.id"(newId) {
      if (newId) {
        this.getEmployees(newId);
        this.updateActiveAssignments();
      }
    },
    ldFlagEmployeePageHorizontalMenu: {
      handler() {
        this.$store.dispatch("featureFlags/checkFeatureFlags", this.$route);
      },
      immediate: true,
    },
    ldFlagEmployeePositionHistoryView: {
      handler(newValue) {
        this.showPositionHistoryTab = newValue;
      },
      immediate: true,
    },
    ldFlagIdeaMdp412: {
      handler(newValue) {
        this.$store.commit("featureFlags/SET_IDEA_MDP_412", newValue);
      },
      immediate: true,
    },
    ldFlagIdeaMdp378: {
      handler(newValue) {
        this.$store.commit("featureFlags/SET_IDEA_MDP_378", newValue);
      },
      immediate: true,
    },
    ldFlagIdeaMdp381: {
      handler(newValue) {
        this.$store.commit("featureFlags/SET_IDEA_MDP_381", newValue);
      },
      immediate: true,
    },
    ldFlagIdeaMdp582: {
      handler(newValue) {
        this.$store.commit("featureFlags/SET_IDEA_MDP_582", newValue === true);
        if (newValue !== true && this.iqSidePanelStore.isOpen) {
          this.iqSidePanelStore.closeSidePanel();
        }
      },
      immediate: true,
    },
    showIQSidePanelShell(isOpen) {
      if (isOpen && !this.$route.meta.secondSidebar) {
        this.sidebarMini = true;
        if (this.isPermanent) {
          this.drawer = false;
        }
      }
    },
    ldFlagIdeaMdp432: {
      handler(newValue) {
        this.$store.commit("featureFlags/SET_IDEA_MDP_432", newValue);
        this.redirectIfTabsDisabled();
      },
      immediate: true,
    },
    ldFlagIdeaMdp438: {
      handler(newValue) {
        this.$store.commit("featureFlags/SET_IDEA_MDP_438", !!newValue);
      },
      immediate: true,
    },
    ldFlagMyStuffNewYourStuff: {
      handler(newValue) {
        this.showNewYourStuffTab = newValue;
      },
      immediate: true,
    },
    ldFlagNewReadiness: {
      handler(newValue) {
        this.showNewReadinessTab = newValue;
        this.redirectIfTabsDisabled();
      },
      immediate: true,
    },
    ldFlagProceduresEnabled: {
      handler(newValue) {
        this.showProceduresTab = newValue;
        this.redirectIfTabsDisabled();
      },
      immediate: true,
    },
    ldFlagFairPayEnabled: {
      handler(newValue) {
        this.showFairPayTab = newValue;
        this.redirectIfTabsDisabled();
      },
      immediate: true,
    },
    ldFlagPerformanceEnabled: {
      handler(newValue) {
        this.showPerformanceTab = newValue;
        this.redirectIfTabsDisabled();
      },
      immediate: true,
    },
  },
  methods: {
    async getAccountCount() {
      try {
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        if (accountID) {
          await updateAccountCountsOnLogin(accountID);
        }
      } catch (error) {
        console.error("Error fetching account counts:", error);
      }
    },
    async initializeLaunchDarklyContext() {
      try {
        // Check if user is logged in and has selected an account
        const token = localStorage.getItem("token");
        const userStr = localStorage.getItem("user");
        const accountId = localStorage.getItem("currentAccountID");
        const accessPermissions = localStorage.getItem("accessPermission");

        if (!token || !userStr || !accountId || !accessPermissions) {
          return; // No valid session, use anonymous context
        }

        const user = JSON.parse(userStr);
        const permissions = JSON.parse(accessPermissions);

        // Create account object from stored data
        const account = {
          id: Number.parseInt(accountId, 10),
          name: permissions.account_name || `Account ${accountId}`,
          owner_email: permissions.owner_email || user?.email,
        };

        // Update LaunchDarkly context
        await this.$identifyTenant(user, account);
      } catch (error) {
        console.error("Error initializing LaunchDarkly context:", error);
      }
    },
    async onSyncAccessPermission() {
      await this.syncAccessPermission();
    },
    async syncAccessPermission(patchUserAccessEnabledOverride) {
      const token = localStorage.getItem("token");
      const accountId = localStorage.getItem("currentAccountID");
      const userStr = localStorage.getItem("user");
      const accessPermissionStr = localStorage.getItem("accessPermission");
      const isLoginPage = this.$route.path === "/login";

      if (!token || !accountId || !userStr || isLoginPage) {
        return;
      }

      try {
        // ER-10317: never re-fetch Access during view-as/preview — Preview's token
        // is the admin user; a refresh would restore Organization nav / mutations.
        if (isInAccountReadOnlySession() || isPreviewModeActive()) {
          if (accessPermissionStr) {
            try {
              this.accessPermissionsValue = JSON.parse(accessPermissionStr);
            } catch {
              this.accessPermissionsValue = null;
            }
          }
          refreshCanStartViewAs();
          return;
        }

        const user = JSON.parse(userStr);
        if (!user?.id) {
          return;
        }

        const patchUserAccessEnabled =
          patchUserAccessEnabledOverride ?? (await this.$getFlag("patch-user-access", false));

        if (accessPermissionStr) {
          const patchedAccessPermission = patchAccessPermission({
            accessPermission: JSON.parse(accessPermissionStr),
            accountId,
            user,
            isEnabled: patchUserAccessEnabled,
          });
          localStorage.setItem("accessPermission", JSON.stringify(patchedAccessPermission));
          this.accessPermissionsValue = patchedAccessPermission;
          syncUserEmployeeInStorage({
            user,
            accessPermission: patchedAccessPermission,
            accountId,
          });
        }

        const refreshedAccessPermission = await refreshAccessPermissionFromApi({
          accountId,
          userId: user.id,
          user,
          patchUserAccessEnabled,
        });
        if (refreshedAccessPermission) {
          this.accessPermissionsValue = refreshedAccessPermission;
        }

        // ER-10317: notify listing menus after accessPermission is patched/refreshed
        // so Co-Owner / Admin see Impersonate (mount ran before this async sync).
        refreshCanStartViewAs();
        try {
          const accessId =
            this.accessPermissionsValue?.id ??
            JSON.parse(localStorage.getItem("accessPermission") || "{}")?.id ??
            null;
          window.dispatchEvent(
            new CustomEvent("exiqtive-access-filters-changed", {
              detail: { accessId },
            })
          );
        } catch (e) {
          /* ignore */
        }
      } catch (error) {
        console.error("Error syncing access permission:", error);
      }
    },
    async refreshFlagsAfterLogin() {
      try {
        // Re-initialize LaunchDarkly context with authenticated user
        await this.initializeLaunchDarklyContext();

        // Refresh all watched flags to get updated values for the new context
        await this.$refreshFlags();

        // Re-fetch specific flag values that are used directly (not via watchers)
        this.showPositionHistoryTab = await this.$getFlag("employee-position-history-view", false);
        this.showReadinessOrganizationTab = await this.$getFlag(
          "hide_organizational_readiness",
          false
        );
        this.showNewYourStuffTab = await this.$getFlag("my-stuff.new-your-stuff.enabled", false);
        this.showNewReadinessTab = await this.$getFlag("new-readiness", false);
        this.showProceduresTab = await this.$getFlag("procedures.enabled", false);
        this.showFairPayTab = await this.$getFlag("fair-pay.enabled", false);
        this.showPerformanceTab = await this.$getFlag("performance.enabled", false);
        this.ideaMDP650 = await this.$getFlag(LD_FLAG_IDEA_MDP_650, false);
        this.tabFlagsLoaded = true;
        this.redirectIfTabsDisabled();
        // Update feature flag store
        const ideaMDP389Flag = await this.$getFlag("idea-MDP-389", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_389", ideaMDP389Flag);
        const ideaMDP378Flag = await this.$getFlag("idea-MDP-378", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_378", ideaMDP378Flag);
        const ideaMDP381Flag = await this.$getFlag("idea-MDP-381", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_381", ideaMDP381Flag);
        const ideaMDP582Flag = await this.$getFlag(LD_FLAG_IDEA_MDP_582, false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_582", ideaMDP582Flag);
        const ideaMDP176Flag = await this.$getFlag("idea-MDP-176", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_176", ideaMDP176Flag);
        const ideaMDP432Flag = await this.$getFlag("idea-MDP-432", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_432", ideaMDP432Flag);
        const ideaMDP441Flag = await this.$getFlag("idea-MDP-441", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_441", ideaMDP441Flag);
        const ideaMDP438Flag = await this.$getFlag("idea-MDP-438", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_438", ideaMDP438Flag);
        // Re-check feature flags for the current route
        await this.$store.dispatch("featureFlags/checkFeatureFlags", this.$route);
      } catch (error) {
        console.error("Error refreshing flags after login:", error);
      }
    },
    handleMouseOver() {
      if (this.$route.meta.secondSidebar) {
        // Employee sidebar hover logic remains the same
        if (this.sidebarMini) {
          this.sidebarMini = false;
        }
      } else if (this.showIQSidePanelShell) {
        this.isHovered = true;
      } else {
        // Main sidebar hover logic
        if (this.isSidebarCollapsedPermanently && !this.isPermanent && !this.toggleMenuClicked) {
          this.sidebarMini = false; // Temporarily expand on hover if permanently collapsed and not on small screen
        }
        this.isHovered = true; // Keep for other potential hover effects
      }
    },
    handleMouseLeave() {
      if (this.$route.meta.secondSidebar) {
        // Employee sidebar hover logic remains the same
        if (!this.sidebarMini) {
          this.sidebarMini = true;
        }
      } else if (this.showIQSidePanelShell) {
        this.isHovered = false;
      } else {
        // Main sidebar hover logic
        if (this.isSidebarCollapsedPermanently && !this.isPermanent && !this.toggleMenuClicked) {
          this.sidebarMini = true; // Revert to mini on mouse leave if permanently collapsed
        }
        this.isHovered = false; // Keep for other potential hover effects
      }
    },
    getEmployeesChatter(accountID, empId) {
      getEmployeeChatter(accountID, empId, 1)
        .then(({ data }) => {
          if (data && data.results) {
            const now = new Date();
            const past48Hours = new Date(now.getTime() - 48 * 60 * 60 * 1000);

            const recentCount = data.results.filter((item) => {
              const createdOn = new Date(item.created_on);
              return createdOn >= past48Hours;
            }).length;
            this.totalChatterCount = recentCount;
          }
        })
        .catch((error) => {
          console.error("Error fetching chatter:", error);
        });
    },
    getEmployeesBadges(accountID, empId) {
      getEmployeeBadges(accountID, empId)
        .then(({ data }) => {
          this.$store.dispatch("employeeStore/currentEmployeeBadges", data);
        })
        .catch((error) => {
          console.error("Error fetching badges:", error);
        });
    }, // ignore
    async getEmployees(empId) {
      // Use centralized employee fetching from Pinia store
      // This prevents duplicate API calls when EmployeeSideBar also fetches the same data
      // ER-11834: `empId` is `$route.params.id`, which is only an employee id on
      // employee detail routes. Non-employee routes (job-families, job-categories)
      // reuse `:id` for other entities — fetching those as an employee 404s and logs
      // four console errors on every load. Skip the fetch entirely in that case.
      if (!this.isEmployeeDetailRoute) {
        return;
      }
      try {
        this.employeeLoading = true;
        const employeeData = await this.employeeStore.fetchCurrentEmployee(empId);
        if (employeeData) {
          // Update Vuex store for backwards compatibility
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.currentReadiness = employeeData?.primary_readiness;
          this.employeesData = employeeData;
        }
      } catch (error) {
        console.error("Error fetching employee data in App.vue:", error);
      } finally {
        this.employeeLoading = false;
      }
    },
    isResponsibilityOpen(route) {
      // ER-10088: the main side-nav auto-collapse must apply ONLY to the positions
      // tree, where a responsibility is opened via the `resId` QUERY param on
      // /organization/positions. Dedicated responsibility routes (e.g.
      // /organization/responsibilities/..., .../roles/:roleId/responsibilities/:resId)
      // carry `resId` as a ROUTE param and must NOT collapse the nav.
      const onPositionsTree =
        route?.path === "/organization/positions" ||
        (route?.name === "NewOrganization" && route?.params?.tab === "positions");
      return Boolean(onPositionsTree && route?.query?.resId);
    },
    applyMainSidebarStateForRoute(route) {
      // ER-10088: collapse only when the user opens a responsibility from the tree
      // during the session. On the first run (initial load / refresh) leave the nav
      // at its default even if resId is already in the URL, so a refresh doesn't
      // come back collapsed.
      if (!this.mainSidebarRouteInitialized) {
        this.mainSidebarRouteInitialized = true;
        this.sidebarMini = this.isSidebarCollapsedPermanently;
        return;
      }
      if (this.isResponsibilityOpen(route)) {
        // ER-10088: opening a responsibility from the tree collapses the main side nav.
        // Remember the user's preference once so it can be restored on close.
        if (!this.responsibilityCollapseActive) {
          this.preResponsibilityCollapseState = this.isSidebarCollapsedPermanently;
          this.responsibilityCollapseActive = true;
        }
        this.isSidebarCollapsedPermanently = true;
        this.sidebarMini = true;
        this.drawer = false;
      } else {
        // ER-10088: closing the responsibility restores the previous nav state.
        if (this.responsibilityCollapseActive) {
          this.isSidebarCollapsedPermanently = this.preResponsibilityCollapseState;
          this.responsibilityCollapseActive = false;
        }
        this.sidebarMini = this.isSidebarCollapsedPermanently; // Use the permanent state for main sidebar
      }
    },
    collapseMainSidebarForAiAssistant() {
      if (this.$route.meta.secondSidebar) {
        return;
      }
      this.isSidebarCollapsedPermanently = true;
      this.sidebarMini = true;
      if (this.isPermanent) {
        this.drawer = false;
      }
    },
    toggleMenu() {
      if (!this.$route.meta.secondSidebar) {
        // For main sidebar, toggle the permanent collapse state
        this.isSidebarCollapsedPermanently = !this.isSidebarCollapsedPermanently;
        this.sidebarMini = this.isSidebarCollapsedPermanently; // Update sidebarMini based on permanent state

        if (this.isPermanent) {
          this.drawer = !this.isSidebarCollapsedPermanently; // Drawer is open if not permanently collapsed
        }

        this.toggleMenuClicked = true;
        setTimeout(() => {
          this.toggleMenuClicked = false;
        }, 300); // Increased timeout duration
      } else {
        // Employee sidebar logic remains the same
        if (!this.isPermanent) {
          this.sidebarMini = !this.sidebarMini;
        } else {
          this.toggleMenuClicked = true;
          this.sidebarMini = !this.sidebarMini;
          if (this.sidebarMini === false) {
            this.drawer = true;
          } else {
            this.drawer = false;
          }
        }
        setTimeout(() => {
          this.$nextTick(() => {
            this.toggleMenuClicked = false;
          });
        }, 100);
      }
    },
    sideToggleMenu() {
      if (this.isPermanent && !this.drawer && !this.toggleMenuClicked) {
        // Only open the drawer if the sidebar is permanently collapsed
        if (this.isSidebarCollapsedPermanently) {
          this.drawer = true;
          this.sidebarMini = false; // Temporarily expand when drawer is open
        }
      }
    },
    handleOutsideClick(event) {
      if (this.isPermanent && this.drawer) {
        const navBarComponent = this.$refs.navigationBar;
        if (navBarComponent) {
          const navBarEl = navBarComponent.$el || navBarComponent;
          if (navBarEl && navBarEl.contains && !navBarEl.contains(event.target)) {
            this.drawer = false;
            // Revert to permanently collapsed state when clicking outside
            this.sidebarMini = this.isSidebarCollapsedPermanently;
          }
        }
      }
    },
    startIdleTimer() {
      window.addEventListener("load", this.resetTimer, true);
      window.addEventListener("mousemove", this.resetTimer, true);
      window.addEventListener("mousedown", this.resetTimer, true);
      window.addEventListener("click", this.resetTimer, true);
      window.addEventListener("keydown", this.resetTimer, true);
      window.addEventListener("scroll", this.resetTimer, true);
      // Start the initial timers
      this.resetTimer();
    },
    resetTimer() {
      if (this.showIdleWarning || this.isImpersonating) {
        return;
      }

      clearTimeout(this.idleTime);
      clearTimeout(this.idleWarningTime);

      this.showIdleWarning = false;

      this.idleWarningTime = setTimeout(() => {
        // Only show warning if user is still logged in
        if (this.isUserLoggedIn) {
          this.showIdleWarning = true;
        }
      }, 3300000);

      this.idleTime = setTimeout(this.logout, 3600000);
    },
    stayLoggedIn() {
      this.resetTimer();
    },
    handleStayLoggedIn() {
      this.showIdleWarning = false;
      this.stayLoggedIn();
    },
    handleLogout() {
      this.logout();
    },
    startImpersonationCountdown() {
      if (!isImpersonationActive()) {
        return;
      }
      if (this.impersonationCountdownTimer) {
        clearInterval(this.impersonationCountdownTimer);
      }
      this.impersonationCountdownTimer = setInterval(() => {
        if (!isImpersonationActive()) {
          this.endImpersonationSession();
          return;
        }
        this.$forceUpdate();
        this.syncImpersonationBannerOffset();
      }, 1000);
    },
    syncImpersonationBannerOffset() {
      const bannerVm = this.$refs.impersonationBanner;
      const el = bannerVm?.$el || bannerVm;
      if (!el || typeof el.getBoundingClientRect !== "function") return;
      const height = Math.ceil(el.getBoundingClientRect().height);
      if (height > 0 && height !== this.impersonationBannerHeightPx) {
        this.impersonationBannerHeightPx = height;
      }
    },
    async endImpersonationSession() {
      // ER-10317: in-account Impersonate/Preview Exit only (stop/). Never call
      // act-as end/ or redirect to the admin app — that race sent DEV to admin login.
      const guard = beginEndImpersonationGuard({
        endingImpersonationSession: this.endingImpersonationSession,
        impersonationCountdownTimer: this.impersonationCountdownTimer,
      });
      this.endingImpersonationSession = guard.endingImpersonationSession;
      this.impersonationCountdownTimer = guard.impersonationCountdownTimer;
      if (!guard.started) {
        return;
      }

      const session = getImpersonationSession();
      const mode = session?.mode;
      const impersonationToken = localStorage.getItem("token");
      let restored = restoreAdminContextFromStash();

      try {
        if (impersonationToken) {
          if (mode === MODE_PREVIEW) {
            await stopPreview(impersonationToken);
          } else {
            await stopViewAs(impersonationToken);
          }
        }
      } catch (error) {
        console.warn("End view-as/preview API failed:", error);
      }

      if (!restored) {
        const stillImpersonating = !!getImpersonationSession()?.isImpersonating;
        restored = !!localStorage.getItem("token") && !stillImpersonating;
      }
      if (!restored) {
        clearImpersonationStorage();
        localStorage.clear();
        window.location.href = "/login";
        return;
      }
      window.location.assign(VIEW_AS_EMPLOYEES_RETURN_PATH);
    },
    logout() {
      this.showIdleWarning = false;
      if (this.isImpersonating) {
        this.endImpersonationSession();
        return;
      }
      if (this.$route.path != "/login") {
        // ER-10191: clear in-memory filter/view singletons before session wipe
        resetInMemoryFilterState();
        // Clear localStorage first
        try {
          const Sentry = require("@sentry/vue").default;
          Sentry.setUser(null);
        } catch (e) {
          console.warn("Sentry not available during logout:", e);
        }
        try {
          const configService = require("@/services/config").default;
          configService.clearSecureConfig();
        } catch (e) {
          console.warn("Config service not available during logout:", e);
        }
        localStorage.clear();
        this.$resetToAnonymous();

        const knock = getKnock();
        if (knock) {
          knock.feeds.teardownInstances();
          knock.teardown();
        }
        this.cleanupKnockGuides();
        if (this.iqOverlayStore.isOpen) {
          this.iqOverlayStore.closeOverlay();
        }
        // The docked IQ side panel lives in App.vue, outside the routed views,
        // so close it on logout too — otherwise it stays open over the login page.
        if (this.iqSidePanelStore.isOpen) {
          this.iqSidePanelStore.closeSidePanel();
        }

        // Navigate directly to login
        this.$router.push({ path: "/login" });
      }
    },
    updateActiveAssignments() {},
    updateCount() {
      const empId = this.$route.params.id;
      this.getEmployees(empId);
    },
    redirectIfTabsDisabled() {
      if (!this.isUserLoggedIn || !this.tabFlagsLoaded) return;

      // Preview: keep the user inside Your Stuff only (view-as uses real role).
      if (this.isPreviewMode) {
        const pathNorm = (this.$route.path || "").replace(/\/$/, "") || "/";
        if (!pathNorm.startsWith("/your-stuff") && pathNorm !== "/impersonation/handoff") {
          this.$router.push("/your-stuff/employee/responsibilities").catch(() => {});
        }
        return;
      }

      try {
        const pathNorm = (this.$route.path || "").replace(/\/$/, "") || "/";
        const raw = localStorage.getItem("accessPermission");
        const perm = raw ? JSON.parse(raw) : {};
        const isOwner = perm?.admin_permission === "owner";
        const isCoOwner = perm?.admin_permission === "co-owner";
        const isOwnerOrCoOwner = isOwner || isCoOwner;
        const isAdmin = perm?.admin_permission === "admin";
        const ideaMDP432 = this.$store.getters["featureFlags/ideaMDP432"];
        const tabs = {
          ideaMDP432,
          showNewOrganizationTab: this.showNewOrganizationTab,
          showNewYourStuffTab: this.showNewYourStuffTab,
          showNewReadinessTab: this.showNewReadinessTab,
          showProceduresTab: this.showProceduresTab,
          showFairPayTab: this.showFairPayTab,
          showPerformanceTab: this.showPerformanceTab,
        };
        const permissions = {
          ideaMDP432,
          isOwner,
          isCoOwner,
          isOwnerOrCoOwner,
          isAdmin,
          hasEmployee: Object.hasOwn(perm, "has_employee") && perm.has_employee === true,
          hasReadinessAccess: perm?.has_readiness_access,
          yourStuffAllowed: isYourStuffAllowed(perm),
        };

        const settingsPath = getSettings432RedirectPath(pathNorm, permissions, tabs);
        if (settingsPath !== undefined) {
          this.$router.push(settingsPath).catch(() => {});
          return;
        }

        const yourStuffDeniedPath = getYourStuffDeniedRedirectPath(pathNorm, permissions, tabs);
        if (yourStuffDeniedPath !== undefined) {
          // ER-12027: the Your Stuff bounce must no longer be silent.
          this.$notifications?.warning?.(
            `Your Stuff isn't available in ${perm?.account_name || "this account"}`
          );
          this.$router.push(yourStuffDeniedPath).catch(() => {});
          return;
        }

        const tabPath = getTabOffRedirectPath(pathNorm, isOwnerOrCoOwner, tabs);
        if (tabPath !== undefined) {
          this.$router.push(tabPath).catch(() => {});
          return;
        }
      } catch (error) {
        console.error("Error in redirectIfTabsDisabled (tabs):", error);
      }

      if (this.$route.path.startsWith("/organization")) {
        try {
          const pathNorm = (this.$route.path || "").replace(/\/$/, "") || "/";
          const readinessCatalogPath = resolveOrganizationReadinessCatalogRedirect(
            pathNorm,
            this.$route,
            this.currentAccountDetails
          );
          if (readinessCatalogPath) {
            this.$router.push(readinessCatalogPath).catch(() => {});
            return;
          }

          const raw = localStorage.getItem("accessPermission");
          const perm = raw ? JSON.parse(raw) : {};
          const isOwner = perm?.admin_permission === "owner";
          const isCoOwner = perm?.admin_permission === "co-owner";
          const isAdmin = perm?.admin_permission === "admin";
          const ideaMDP432 = this.$store.getters["featureFlags/ideaMDP432"];
          const permissions = {
            isOwnerOrCoOwner: isOwner || isCoOwner,
            isAdmin,
            yourStuffAllowed: isYourStuffAllowed(perm),
            hasReadinessAccess: perm?.has_readiness_access,
          };
          const tabs = {
            ideaMDP432,
            showNewOrganizationTab: this.showNewOrganizationTab,
            showNewYourStuffTab: this.showNewYourStuffTab,
            showNewReadinessTab: this.showNewReadinessTab,
            showPerformanceTab: this.showPerformanceTab,
            showFairPayTab: this.showFairPayTab,
          };
          const orgPath = getOrganizationRedirectPath(permissions, tabs);
          if (orgPath !== undefined) this.$router.push(orgPath).catch(() => {});
        } catch (error) {
          console.error("Error in redirectIfTabsDisabled (organization):", error);
        }
      }
    },
  },
};
</script>
<style>
.v-main__wrap {
  display: flex;
}

.application_style {
  font-family: "Inter", sans-serif !important;
}

.ex-main-container {
  max-height: 100vh;
  scrollbar-width: none;
}

.ex-main-container--scroll {
  overflow-y: scroll;
}

.ex-main-container--phone-shell {
  max-height: calc(100vh - 53px);
}

.ex-main-container--with-iq-panel {
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  overflow: hidden;
}

.ex-main-container__body {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.ex-main-container__body--scroll {
  overflow-y: auto;
  max-height: 100vh;
  scrollbar-width: none;
}

.ex-main-container__body--scroll::-webkit-scrollbar {
  display: none;
}

.ex-main-container::-webkit-scrollbar {
  display: none;
}

.desktop-only {
  font-size: 36px;
  font-weight: 600;
  line-height: 44px;
  letter-spacing: -0.02em;
  text-align: center;
  color: #475467;
}

.only-desktop-message {
  font-size: 18px;
  font-weight: 400;
  line-height: 28px;
  text-align: center;
  color: #475467;
}

.toggle-button {
  position: absolute;
  right: -13px;
  top: 43px;
  z-index: 9;
}

.plus-icon-small {
  vertical-align: middle;
}

.v-navigation-drawer {
  overflow: visible;
}

.right-side-card {
  position: relative;
  top: 25px;
  left: -15px;
}

.employee-status-side {
  left: 120px;
  top: -10px;
}

.employee-back-icon {
  top: -29px;
}

.employee-three-dots {
  top: -27px;
}

.toggle-button-class {
  display: flex;
  padding: 3.673px;
  justify-content: center;
  align-items: center;
  gap: 5.878px;
  border-radius: 5.878px;
  border: 0.735px solid #7f56d9;
  background: #7f56d9;
  box-shadow: 0px 0.735px 1.469px 0px rgba(16, 24, 40, 0.05);
}

.toggle-button-class:hover {
  background-color: #5b2cc5;
  cursor: url("../assets/cursorArrow.svg"), auto;
}

.impersonation-active-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  border-radius: 0;
  width: 100%;
}
.impersonation-active-banner.ex-generic-banner {
  background-color: #fff8e1;
  border-bottom: 1px solid #f0e6b8;
}

/* Push all app content down when banner is active so nothing is hidden underneath */
.impersonation-banner-offset .v-navigation-drawer {
  top: var(--impersonation-banner-height, 110px) !important;
  height: calc(100% - var(--impersonation-banner-height, 110px)) !important;
}
.impersonation-banner-offset .v-main {
  padding-top: calc(
    var(--v-layout-top, 0px) + var(--impersonation-banner-height, 110px)
  ) !important;
}

.el-notification__content {
  font-family: Inter, sans-serif !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 20px !important;
  letter-spacing: normal;
  color: #475467 !important;
  text-align: unset !important;
  margin: unset !important;
}

.side-menu-nav {
  transition: width 0.3s ease-in-out !important;
}
</style>
